import { useTranslation } from "react-i18next";
import { SessionTime } from "./SessionTime";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDateWithOptionsBs } from "@/lib/format";
import { CheckCircle, CalendarDots, X } from "@phosphor-icons/react";
import type { ServiceInfo } from "./types";
import { wallClockToUtc } from "@/lib/timezone";

export function BookingSuccess({
  therapistName,
  service,
  date,
  dateStr,
  time,
  onClose,
  paymentMethod,
  bankDetails,
  modality,
  officeAddress,
}: {
  therapistName: string;
  service: ServiceInfo;
  date: Date;
  dateStr: string;
  time: string;
  onClose?: () => void;
  paymentMethod: "monri" | "bank_transfer" | "company_credit";
  bankDetails?: {
    bankName: string;
    accountNumber: string;
    recipientName: string;
    reference: string;
    amount: number;
    currency: string;
  };
  modality: "online" | "in_person";
  officeAddress?: string | null;
}) {
  const { t } = useTranslation();

  // Build the UTC moment from Sarajevo wall-clock so the iCalendar export
  // and Google Calendar URL are correct regardless of the browser's timezone.
  const startDt = new Date(wallClockToUtc(dateStr, time));
  const endDt = new Date(startDt.getTime() + service.durationMinutes * 60000);
  // iCalendar/Google Calendar UTC format: YYYYMMDDTHHMMSSZ
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const title = encodeURIComponent(`${service.name}, ${therapistName}`);
  const details = encodeURIComponent(t("booking.calendarDescription", { therapistName, serviceName: service.name, duration: service.durationMinutes }));

  const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(startDt)}/${fmt(endDt)}&details=${details}`;

  const icsContent = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "BEGIN:VEVENT",
    `DTSTART:${fmt(startDt)}`,
    `DTEND:${fmt(endDt)}`,
    `SUMMARY:${service.name}, ${therapistName}`,
    `DESCRIPTION:${t("booking.calendarDescription", { therapistName, serviceName: service.name, duration: service.durationMinutes })}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const icsBlob = new Blob([icsContent], { type: "text/calendar" });
  const icsUrl = URL.createObjectURL(icsBlob);

  return (
    <div className="lg:rounded-2xl lg:border bg-card lg:shadow-sm">
      {onClose && (
        <div className="flex justify-end px-4 pt-3">
          <Button variant="ghost" size="icon-sm" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>
      )}
      <div className={cn("flex flex-col items-center justify-center px-6 pb-12 text-center", onClose ? "pt-2" : "pt-12")}>
        <div className="flex size-16 items-center justify-center rounded-full" style={{ background: "oklch(92% 0.05 155)" }}>
          <CheckCircle className="size-8" style={{ color: "oklch(55% 0.15 155)" }} />
        </div>
        <h3 data-testid="booking-success-title" className="mt-5 text-xl font-bold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
          {paymentMethod === "bank_transfer" ? t("booking.successTitleTransfer") : t("booking.successTitle")}
        </h3>
        <p className="mt-2 max-w-xs text-sm text-muted-foreground">
          {paymentMethod === "bank_transfer"
            ? t("booking.successMessageTransfer", { therapistName })
            : t("booking.successMessage", { therapistName })
          }
        </p>
        {paymentMethod === "company_credit" && (
          <p className="mt-2 max-w-xs text-sm text-muted-foreground">
            {t("booking.successCompanyPaid")}
          </p>
        )}

        {/* Session details */}
        <div className="mt-5 w-full max-w-xs rounded-xl p-3 text-left text-sm" style={{ background: "oklch(97% 0.01 84)" }}>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t("booking.dateLabel")}</span>
            <span className="font-medium text-foreground">
              {formatDateWithOptionsBs(date, { day: "numeric", month: "short", year: "numeric" })}
            </span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-muted-foreground">{t("booking.timeLabel")}</span>
            <span className="font-medium text-foreground"><SessionTime dateStr={dateStr} time={time} /></span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {modality === "in_person"
              ? `${t("booking.modalityInPerson")}${officeAddress ? `, ${officeAddress}` : ""}`
              : t("booking.modalityOnline")}
          </p>
        </div>

        {paymentMethod === "bank_transfer" && bankDetails && (
          <div className="mt-4 w-full max-w-xs">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-left">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-700">
                {t("booking.bankTransferTitle")}
              </p>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("booking.bankName")}</span>
                  <span className="font-medium text-foreground">{bankDetails.bankName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("booking.accountNumber")}</span>
                  <span className="font-mono text-xs font-medium text-foreground">{bankDetails.accountNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("booking.recipient")}</span>
                  <span className="font-medium text-foreground">{bankDetails.recipientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("booking.reference")}</span>
                  <span className="font-mono text-xs font-semibold text-foreground">{bankDetails.reference}</span>
                </div>
                <div className="flex justify-between border-t border-amber-200 pt-1.5">
                  <span className="font-semibold text-foreground">{t("booking.priceLabel")}</span>
                  <span className="font-bold text-foreground">{bankDetails.amount} {bankDetails.currency}</span>
                </div>
              </div>
              <p className="mt-3 text-[11px] text-amber-600">
                {t("booking.bankTransferNote")}
              </p>
            </div>
          </div>
        )}

        {/* Add to calendar */}
        <div className="mt-5 w-full max-w-xs space-y-2">
          <p className="text-xs font-medium text-muted-foreground">{t("booking.addToCalendar")}</p>
          <a
            href={googleUrl}
            target="_blank"
            rel="noreferrer"
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-border/60 bg-background px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            <CalendarDots className="size-4" />
            Google Calendar
          </a>
          <a
            href={icsUrl}
            download={`sesija-${dateStr}.ics`}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-border/60 bg-background px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            <CalendarDots className="size-4" />
            Apple / Outlook (.ics)
          </a>
        </div>
      </div>
    </div>
  );
}
